import type { RouteLocationRaw } from 'vue-router';
import { matrixQueueStructureApi, type MatrixQueueStructure } from '@/features/FormBuilder';
import { DocumentType } from '@/shared/lib';

/**
 * Screens that open a Registration read-only: the two homologation stages and Diretórios. Every
 * difference between them lives right here: the screen itself names no stage. An unknown value
 * resolves to the approval stage, so a route may only declare a stage that is already a key of the
 * map below.
 */
export type RecordViewStage = 'approval' | 'verification' | 'directory';

export interface RecordViewStageConfig {
  /** Middle breadcrumb crumb, and the label the route meta prints while the code loads. */
  stageLabel: string;
  /** Where the browser back button lands, and where a failed load sends the user. */
  listRoute: RouteLocationRaw;
  fetchStructure: (matrixId: number) => Promise<MatrixQueueStructure>;
  loadErrorTitle: string;
  loadErrorMessage: string;
}

export const RECORD_VIEW_STAGE_CONFIG: Record<RecordViewStage, RecordViewStageConfig> = {
  approval: {
    stageLabel: 'Etapa de Análise Crítica e Aprovação',
    listRoute: { path: '/aprovacao', query: { type: DocumentType.REGISTRATION } },
    fetchStructure: (matrixId) => matrixQueueStructureApi.getAwaitingApproval(matrixId),
    loadErrorTitle: 'Erro ao carregar registro',
    loadErrorMessage: 'Não foi possível abrir o formulário deste registro.',
  },
  verification: {
    stageLabel: 'Etapa de Verificação e Vigência',
    listRoute: { path: '/verificacao', query: { type: DocumentType.REGISTRATION } },
    fetchStructure: (matrixId) => matrixQueueStructureApi.getAwaitingVerification(matrixId),
    loadErrorTitle: 'Erro ao carregar registro',
    loadErrorMessage: 'Não foi possível abrir o formulário deste registro.',
  },
  directory: {
    stageLabel: 'Diretórios',
    listRoute: { name: 'Diretorios' },
    fetchStructure: (matrixId) => matrixQueueStructureApi.getDirectoryMatrix(matrixId),
    loadErrorTitle: 'Erro ao carregar registro',
    loadErrorMessage: 'Não foi possível abrir o formulário deste registro.',
  },
};

const isRecordViewStage = (value: unknown): value is RecordViewStage =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(RECORD_VIEW_STAGE_CONFIG, value);

export const getRecordViewStageConfig = (stage?: unknown): RecordViewStageConfig =>
  isRecordViewStage(stage)
    ? RECORD_VIEW_STAGE_CONFIG[stage]
    : RECORD_VIEW_STAGE_CONFIG.approval;
